import { useAdminCancelAndRefundReservation } from "@/api/admin/useAdminCancelAndRefundReservation";
import { useAdminCancelReservation } from "@/api/admin/useAdminCancelReservation";
import { ReservationRequestDto } from "@/api/generated/generatedTypes";
import { ViewAsUserButton } from "@/components/ViewAs/ViewAsUserButton";
import { calculateAge } from "@/utils/date";
import { getReservationStatusInfo } from "@/utils/reservationStatusHelper";
import { Accordion, ActionIcon, Badge, Button, Group, Stack, Text } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import { MatchIndicators } from "../components/MatchIndicators";
import { PreferredLocationsDisplay } from "../components/PreferredLocationsDisplay";
import { PreferredTimesDisplay } from "../components/PreferredTimesDisplay";
import { UserOverviewModal } from "../components/UserOverviewModal";
import { showErrorNotification, showSuccessNotification } from "../utils/errorNotifications";
import { AttendeeItemProps, formatTime } from "./types";

const GENDER_LABELS: Record<string, string> = {
  MALE: "남성",
  FEMALE: "여성",
  NON_BINARY: "논바이너리",
};

export const AttendeeItem = ({
  request,
  eventStartTime,
  eventLocationName,
  isStaging,
  onRemove,
  onChangeRequest,
  onCancel,
}: AttendeeItemProps) => {
  const { mutateAsync: cancelReservation } = useAdminCancelReservation();
  const { mutateAsync: cancelAndRefundReservation } = useAdminCancelAndRefundReservation();
  const [isCanceling, setIsCanceling] = useState(false);
  const [isForceRefunding, setIsForceRefunding] = useState(false);
  const [overviewRequest, setOverviewRequest] = useState<ReservationRequestDto | null>(null);

  // Calculate matches
  const dateMatches = request.preferredTimes.some((time) => {
    const timeString = typeof time === "string" ? time : (time as Date).toISOString();
    const preferredDate = new Date(timeString).toISOString();
    const eventDate = new Date(eventStartTime).toISOString();
    return preferredDate === eventDate;
  });

  const locationMatches = request.preferredLocations.some((loc) => loc.name === eventLocationName);

  // Get status badge
  const getStatusBadge = () => {
    if (isStaging) {
      return (
        <Badge color="yellow" size="sm">
          STAGING
        </Badge>
      );
    }

    const { label, color } = getReservationStatusInfo({
      status: request.status,
      cancellationCategory: request.cancellationCategory,
    });

    return (
      <Badge size="sm" style={{ backgroundColor: color, color: "white" }}>
        {label}
      </Badge>
    );
  };

  const handleCancel = async () => {
    if (!onCancel) return;

    if (
      !window.confirm(
        `${request.user.name}님의 예약을 취소하시겠습니까? 이 작업은 되돌릴 수 없습니다.`
      )
    ) {
      return;
    }

    setIsCanceling(true);
    try {
      await cancelReservation(request.id);
      showSuccessNotification("예약 취소 완료", "예약이 성공적으로 취소되었습니다");
      onCancel(request.id);
    } catch (error) {
      console.error("Failed to cancel reservation:", error);
      showErrorNotification(error, "예약 취소 실패");
    } finally {
      setIsCanceling(false);
    }
  };

  const handleForceRefundCancel = async () => {
    if (!onCancel) return;

    if (
      !window.confirm(
        `${request.user.name}님의 예약을 취소하고 환불 기한과 관계없이 전액 환불하시겠습니까? 이 작업은 되돌릴 수 없습니다.`
      )
    ) {
      return;
    }

    setIsForceRefunding(true);
    try {
      const result = await cancelAndRefundReservation(request.id);
      if (result.refundStatus === "NO_REFUND") {
        showSuccessNotification(
          "예약 취소 완료",
          "예약은 취소되었지만 환불할 결제 내역이 없습니다"
        );
      } else {
        showSuccessNotification(
          "강제 환불 완료",
          "예약이 취소되었고 환불이 처리되었습니다"
        );
      }
      onCancel(request.id);
    } catch (error) {
      showErrorNotification(error, "강제 환불 실패");
    } finally {
      setIsForceRefunding(false);
    }
  };

  return (
    <>
      <Accordion.Item value={request.id.toString()}>
        <Accordion.Control>
          <Group justify="space-between" wrap="nowrap">
            <div style={{ flex: 1 }}>
              <Group gap="xs" mb={4}>
                <Text c="black" size="sm" fw={600}>
                  {request.user.name} ({request.user.nickname})
                </Text>
                {calculateAge(request.user.birthDate) !== null && (
                  <Text c="dimmed" size="xs">
                    {calculateAge(request.user.birthDate)}세
                  </Text>
                )}
                <Text c="dimmed" size="xs">
                  {GENDER_LABELS[request.user.gender] ?? request.user.gender}
                </Text>
                {getStatusBadge()}
                {request.user.isTestUser && (
                  <Badge size="sm" color="grape">
                    TEST
                  </Badge>
                )}
              </Group>
              <MatchIndicators dateMatches={dateMatches} locationMatches={locationMatches} />
            </div>
            <Group gap="xs" wrap="nowrap">
              <ViewAsUserButton
                userId={request.user.uid}
                userName={request.user.name}
                isTestUser={request.user.isTestUser}
              />
              <ActionIcon
                variant="subtle"
                size="lg"
                mr="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  setOverviewRequest(request);
                }}
              >
                <IconSearch size={15} />
              </ActionIcon>
              {isStaging && onRemove && (
                <Button
                  size="xs"
                  variant="light"
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(request.id);
                  }}
                >
                  제거
                </Button>
              )}
            </Group>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="md" pl="md">
            <PreferredTimesDisplay
              preferredTimes={request.preferredTimes.map((t) =>
                typeof t === "string" ? t : (t as Date).toISOString()
              )}
              formatTime={formatTime}
              selectedDate={eventStartTime}
            />

            <PreferredLocationsDisplay
              preferredLocations={request.preferredLocations}
              selectedLocationId={null}
              selectedLocationName={eventLocationName}
            />

            {!isStaging &&
              ["CONFIRMED", "PREFERENCE_OVERRIDE_REQUESTED"].includes(request.status) && (
                <Group gap="sm">
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => onChangeRequest && onChangeRequest(request.id)}
                  >
                    변경요청
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    color="red"
                    onClick={handleCancel}
                    loading={isCanceling}
                    disabled={isForceRefunding}
                  >
                    취소/환불
                  </Button>
                  <Button
                    size="xs"
                    variant="filled"
                    color="red"
                    onClick={handleForceRefundCancel}
                    loading={isForceRefunding}
                    disabled={isCanceling}
                  >
                    강제 환불
                  </Button>
                </Group>
              )}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>

      <UserOverviewModal request={overviewRequest} onClose={() => setOverviewRequest(null)} />
    </>
  );
};
